import { nanoid } from 'nanoid/non-secure';
import { createStore } from 'zustand';

import {
  AdElementSchema,
  AdElementText,
  AdElement,
  AdTemplate,
  BrandVariable,
  Datasources,
  EditorState,
  EditorStateActions,
  InvalidElementContentMapping,
  isCasinoAssetElement,
  isImageElement,
  isSVGElement,
  isTeamAssetElement,
  isTextElement,
  isVectorElement,
} from '@adflow/types';
import { Element, SVG } from '@svgdotjs/svg.js';
import {
  applyAnimationEffect as applyPresetEffect,
  removeAnimationEffect as removePresetEffect,
  supportsTemplateAnimations,
  updateAnimationEffect as updatePresetEffect,
} from '../animation';
import {
  getVariantLabel,
  normalizeVariantLabel,
} from '../components/Rightsidebar/TextElementControls/utils/fontUtils';

type StoreSet = (
  partial:
    | EditorState
    | Partial<EditorState>
    | ((state: EditorState) => EditorState | Partial<EditorState>),
  replace?: boolean | undefined,
) => void;

type Get = () => EditorState;

const TEMPLATE_ASSETS_PREFIX = 'https://template-assets';

export const DATA_SOURCE_REMOVED = 'DATA_SOURCE_REMOVED';

type FontVariant = {
  weight: number;
  style: string;
  stretch: string;
};

type InvalidFontSelection = ReturnType<EditorState['getInvalidFontSelections']>[number];

const normalizeFontVariant = (variant: Partial<FontVariant>) => {
  const style = variant.style && variant.style.trim() !== '' ? variant.style : 'normal';
  const stretch = variant.stretch && variant.stretch.trim() !== '' ? variant.stretch : 'normal';

  return {
    weight: variant.weight ?? 400,
    style,
    stretch,
  };
};

const buildCustomFontVariantMap = (
  customFonts: EditorState['customFonts'],
): Map<string, FontVariant[]> => {
  const map = new Map<string, Map<string, FontVariant>>();

  customFonts.forEach((font) => {
    // For custom fonts, preserve the exact family name without normalization
    const family = font.fontFamily;
    if (!family) return;
    const normalized = normalizeFontVariant({
      weight: font.weight,
      style: font.style,
      stretch: font.stretch,
    });
    // Use the explicit stretch value from the font metadata
    const finalStretch = normalized.stretch;
    const key = `${normalized.weight}:${normalized.style}:${finalStretch}`;
    let familyMap = map.get(family);
    if (!familyMap) {
      familyMap = new Map();
      map.set(family, familyMap);
    }
    familyMap.set(key, { ...normalized, stretch: finalStretch });
  });

  const result = new Map<string, FontVariant[]>();
  map.forEach((variants, family) => {
    result.set(family, Array.from(variants.values()));
  });

  return result;
};

const buildCustomFontVariantLabelMap = (
  customFonts: EditorState['customFonts'],
): Map<string, Set<string>> => {
  const map = new Map<string, Set<string>>();

  customFonts.forEach((font) => {
    // For custom fonts, preserve the exact family name without normalization
    const family = font.fontFamily;
    if (!family) return;

    const weight = font.weight ?? 400;
    const style = font.style && font.style.trim() !== '' ? font.style : 'normal';
    const label = normalizeVariantLabel(getVariantLabel(weight, style, font.variant));
    if (!label) return;

    let labelSet = map.get(family);
    if (!labelSet) {
      labelSet = new Set();
      map.set(family, labelSet);
    }
    labelSet.add(label);
  });

  return map;
};

const hasExactVariant = (variants: FontVariant[], target: FontVariant) =>
  variants.some(
    (variant) =>
      variant.weight === target.weight &&
      variant.style === target.style &&
      variant.stretch === target.stretch,
  );

const hasUniqueVariantIgnoringStretch = (variants: FontVariant[], target: FontVariant) => {
  const matches = variants.filter(
    (variant) => variant.weight === target.weight && variant.style === target.style,
  );
  return matches.length === 1;
};

export const createEditorStore = (defaultStoreValues?: Partial<EditorState>) => {
  const createInitialState = (get: Get, set: StoreSet) =>
    ({
      name: defaultStoreValues?.name || 'New Ad Template',
      thumbnailURL: '',
      bgColour: defaultStoreValues?.bgColour || '',
      elements: defaultStoreValues?.elements || [],
      selectedElementId: defaultStoreValues?.selectedElementId || null,
      dataSources: defaultStoreValues?.dataSources || [],
      brand: defaultStoreValues?.brand || null,
      customFonts: defaultStoreValues?.customFonts || [],
      displayOptions: defaultStoreValues?.displayOptions || {
        teamAssets: {
          type: 'JERSEY',
          fallback: 'NONE',
        },
        dateTimeFormat: {
          timeFormat: 'hour12',
          dayFormat: 'long',
          monthFormat: 'long',
          showTimezone: true,
          countdownFormatStyle: 'digital',
          countdownTimeGranularity: 'H_M_S',
          isTextFormat: false,
        },
        odds: {
          type: 'decimalOdds',
        },
      },
      businessEntityId: defaultStoreValues?.businessEntityId || null,
      userId: defaultStoreValues?.userId || null,
      importSourceJSON: defaultStoreValues?.importSourceJSON || '',
      importSourceType:
        defaultStoreValues?.importSourceType ?? ('figma' as AdTemplate['importSourceType']),
      videoProjectId: defaultStoreValues?.videoProjectId || null,
      brandKitId: defaultStoreValues?.brandKitId || null,
      brandKit: defaultStoreValues?.brandKit || null,
      animation: supportsTemplateAnimations(
        defaultStoreValues?.importSourceType ?? ('figma' as AdTemplate['importSourceType']),
      )
        ? defaultStoreValues?.animation ?? null
        : null,
      animationPlaybackToken: 0,
      ...storeActions(set, get),
    } satisfies EditorState);

  const s = createStore<ReturnType<typeof createInitialState>>()((set, get) =>
    createInitialState(get, set),
  );

  return s;
};

function storeActions(set: StoreSet, get: Get) {
  const actions = {
    setName: (name) => set(() => ({ name })),
    setBgColour: (bgColour) => set(() => ({ bgColour })),
    addElement: (el) =>
      set((state) => {
        if (!el.sources) el.sources = [];
        return { elements: [...state.elements, el] };
      }),
    removeNodesFromSVG: removeNodesFromSVG(set, get),
    getImageLinkForImageElementNameFromSVG: getImageLinkForImageElementNameFromSVG(set, get),
    generateUniqueIDs: generateUniqueIDs(set, get),
    setElementSelected: (maybeId) => {
      set(() => {
        return {
          selectedElementId: maybeId,
        };
      });
    },
    removeElement: (id) => {
      set((state) => {
        const elements = state.elements.filter((el) => el.id !== id);
        const effects = state.animation?.effects ?? [];
        const remainingEffects = effects.filter((effect) => effect.targetId !== id);

        if (!state.animation || remainingEffects.length === effects.length) {
          return { elements, selectedElementId: null };
        }

        if (remainingEffects.length === 0) {
          return { elements, selectedElementId: null, animation: null };
        }

        return {
          elements,
          selectedElementId: null,
          animation: {
            ...state.animation,
            tracks: state.animation.tracks.filter((track) => track.targetId !== id),
            effects: remainingEffects,
          },
        };
      });
    },
    applyAnimationEffect: (targetId, presetId, params?) => {
      if (!supportsTemplateAnimations(get().importSourceType)) {
        return;
      }
      const animation = applyPresetEffect(get().animation, {
        targetId,
        presetId,
        params,
      });
      set({ animation });
    },
    updateAnimationEffect: (effectId, params) => {
      if (!supportsTemplateAnimations(get().importSourceType)) {
        return;
      }
      const current = get().animation;
      if (!current) {
        throw new Error('No animation to update');
      }
      set({ animation: updatePresetEffect(current, effectId, params) });
    },
    removeAnimationEffect: (effectId) => {
      if (!supportsTemplateAnimations(get().importSourceType)) {
        return;
      }
      const current = get().animation;
      if (!current) {
        throw new Error('No animation to remove');
      }
      set({ animation: removePresetEffect(current, effectId) });
    },
    replayAnimation: () => {
      if (!supportsTemplateAnimations(get().importSourceType)) {
        return;
      }
      set((state) => ({ animationPlaybackToken: state.animationPlaybackToken + 1 }));
    },
    addElements: (els) => set(() => ({ elements: els })),
    addDataSource: (type, dataType = 'UNKNOWN', id = nanoid(11)) =>
      set((state) => ({
        dataSources: [{ id, type, dataType }, ...state.dataSources],
      })),
    removeDataSource: (id) =>
      set((state) => ({
        dataSources: state.dataSources.filter((source) => source.id !== id),
      })),
    updateElementName: (id, updatedName) =>
      set((state) => {
        const itemIndex = state.elements.findIndex((el) => el.id === id);

        if (itemIndex == -1) {
          console.warn(`Tried to update element with id:${id} but no element was found.`);
          return {};
        }

        const element = state.elements[itemIndex];

        const updatedElement = {
          ...element,
          name: updatedName,
        };

        return {
          elements: insertItem(state.elements, itemIndex, updatedElement),
        };
      }),
    updateElementPosition: (id, x, y) =>
      set((state) => {
        const itemIndex = state.elements.findIndex((el) => el.id === id);

        if (itemIndex == -1) {
          console.warn(`Tried to update element with id:${id} but no element was found.`);
          return {};
        }

        const element = state.elements[itemIndex];
        const updatedElement = AdElementSchema.parse({
          ...element,
          position: {
            x,
            y,
            z: element.position.z,
          },
        });
        return {
          elements: insertItem(state.elements, itemIndex, updatedElement),
        };
      }),
    updateElementData: (id, updatedData) =>
      set((state) => {
        const itemIndex = state.elements.findIndex((el) => el.id === id);

        if (itemIndex == -1) {
          console.warn(`Tried to update element with id:${id} but no element was found.`);
          return {};
        }

        const element = state.elements[itemIndex];

        const updatedElement = AdElementSchema.parse({
          ...element,
          data: updatedData,
        });

        return {
          elements: insertItem(state.elements, itemIndex, updatedElement),
        };
      }),
    updateElementDataSources: (id, dataSources) =>
      set((state) => {
        const itemIndex = state.elements.findIndex((existing) => existing.id === id);

        if (itemIndex == -1) {
          console.warn(`Tried to update element with id:${id} but no element was found.`);
          return {};
        }

        const element = state.elements[itemIndex];

        return {
          elements: insertItem(state.elements, itemIndex, {
            ...element,
            sources: dataSources,
          }),
        };
      }),
    removeElementDataSource: (id, dataSourceId) =>
      set((state) => {
        const itemIndex = state.elements.findIndex((existing) => existing.id === id);

        if (itemIndex == -1) {
          console.warn(`Tried to update element with id:${id} but no element was found.`);
          return {};
        }

        const element = { ...state.elements[itemIndex] };

        // Remove any templated strings from the text element
        if (isTextElement(element)) {
          element.data.content = DATA_SOURCE_REMOVED;
        }

        return {
          elements: insertItem(state.elements, itemIndex, {
            ...element,
            sources: element.sources?.filter((source) => source.sourceId !== dataSourceId),
          }),
        };
      }),
    addBrand: (brand) => set(() => ({ brand })),
    setCustomFonts: (customFonts) => set(() => ({ customFonts })),
    setDisplayOptions: (key, value) => {
      return set((state) => ({
        displayOptions: { ...state.displayOptions, [key]: value },
      }));
    },
    setElementDateDisplayOptions: (elementId, value) => {
      return set((state) => {
        const elementIndex = state.elements.findIndex((el) => el.id === elementId);

        if (elementIndex === -1) {
          console.warn(
            `Tried to update Date display options for element with id:${elementId} but no element was found.`,
          );
          return {};
        }

        const element = state.elements[elementIndex] as AdElementText;
        const updatedElement = {
          ...element,
          data: {
            ...element.data,
            dateDisplayOptions: value,
          },
        };

        return {
          elements: insertItem(state.elements, elementIndex, updatedElement),
        };
      });
    },
    setElementOddsDisplayOptions: (elementId, value) => {
      return set((state) => {
        const elementIndex = state.elements.findIndex((el) => el.id === elementId);

        if (elementIndex === -1) {
          console.warn(
            `Tried to update Odds display options for element with id:${elementId} but no element was found.`,
          );
          return {};
        }

        const element = state.elements[elementIndex] as AdElementText;
        const updatedElement = {
          ...element,
          data: {
            ...element.data,
            odds: value,
          },
        };

        return {
          elements: insertItem(state.elements, elementIndex, updatedElement),
        };
      });
    },
    setElementPenaltiesFormat: (elementId, value) => {
      return set((state) => {
        const elementIndex = state.elements.findIndex((el) => el.id === elementId);

        if (elementIndex === -1) {
          console.warn(
            `Tried to update Penalties format for element with id:${elementId} but no element was found.`,
          );
          return {};
        }

        const element = state.elements[elementIndex] as AdElementText;
        const updatedElement = AdElementSchema.parse({
          ...element,
          data: {
            ...element.data,
            penaltiesFormat: value,
          },
        });

        return {
          elements: insertItem(state.elements, elementIndex, updatedElement),
        };
      });
    },
    setThumbnailURL: (url) => set(() => ({ thumbnailURL: url })),
    getInvalidFontSelections: () => {
      const state = get();
      if (state.importSourceType === 'creatomate') {
        return [];
      }

      const textElements = state.elements.filter(isTextElement);
      const customFontFamilies = state.customFonts.map((f) => f.fontFamily);
      const allFonts = [...customFontFamilies];
      // const allFonts = [...state.standardFonts, ...customFontFamilies];
      const variantsByFamily = buildCustomFontVariantMap(state.customFonts);
      const variantLabelsByFamily = buildCustomFontVariantLabelMap(state.customFonts);

      const invalidFonts: Array<InvalidFontSelection | null> = textElements.map((el) => {
        const family = el.data.style.fontFamily;
        if (family === BrandVariable.PRIMARY_FONT || family === BrandVariable.SECONDARY_FONT) {
          return null;
        }

        if (!family || !allFonts.includes(family)) {
          return {
            elementId: el.id,
            elementName: el.name,
            fontFamily: null,
            fontVariant: el.data.style.fontVariant ?? null,
            fontWeight: el.data.style.fontWeight ?? null,
            fontStyle: el.data.style.fontStyle ?? null,
            fontStretch: el.data.style.fontStretch ?? null,
            reason: 'missing-family' as const,
          };
        }

        // For custom fonts, use exact family name without normalization
        const variants = variantsByFamily.get(family);
        if (!variants || !variants.length) {
          return {
            elementId: el.id,
            elementName: el.name,
            fontFamily: family,
            fontVariant: el.data.style.fontVariant ?? null,
            fontWeight: el.data.style.fontWeight ?? null,
            fontStyle: el.data.style.fontStyle ?? null,
            fontStretch: el.data.style.fontStretch ?? null,
            reason: 'missing-variant' as const,
          };
        }

        const currentVariantLabel = normalizeVariantLabel(el.data.style.fontVariant ?? null);
        if (currentVariantLabel) {
          const labels = variantLabelsByFamily.get(family);
          if (!labels || !labels.has(currentVariantLabel)) {
            return {
              elementId: el.id,
              elementName: el.name,
              fontFamily: family,
              fontVariant: el.data.style.fontVariant ?? null,
              fontWeight: el.data.style.fontWeight ?? null,
              fontStyle: el.data.style.fontStyle ?? null,
              fontStretch: el.data.style.fontStretch ?? null,
              reason: 'missing-variant' as const,
            };
          }
          return null;
        }

        const current = normalizeFontVariant({
          weight: el.data.style.fontWeight ?? undefined,
          style: el.data.style.fontStyle ?? undefined,
          stretch: el.data.style.fontStretch ?? undefined,
        });
        // For custom fonts, use the explicit stretch value from element
        const normalizedCurrent = current;

        if (!hasExactVariant(variants, normalizedCurrent)) {
          if (hasUniqueVariantIgnoringStretch(variants, normalizedCurrent)) {
            return null;
          }
          return {
            elementId: el.id,
            elementName: el.name,
            fontFamily: family,
            fontVariant: el.data.style.fontVariant ?? null,
            fontWeight: normalizedCurrent.weight,
            fontStyle: normalizedCurrent.style,
            fontStretch: normalizedCurrent.stretch,
            reason: 'missing-variant' as const,
          };
        }

        return null;
      });

      return invalidFonts.filter((item): item is NonNullable<typeof item> => item !== null);
    },
    getInvalidElementContentMappings: () => {
      const state = get();
      return getInvalidElementContentMappings(state.elements, state.dataSources);
    },
    validateSelectedFonts: () => {
      const state = get();
      // Validate fonts only if not creatomate template - because fonts are not used in creatomate templates
      if (state.importSourceType === 'creatomate') {
        return true;
      }
      return state.getInvalidFontSelections().length === 0;
    },
    validateContentTemplateLinks: () => {
      const imagesWithInvalidLinks = get().elements.filter((el) => {
        if (el.type !== 'IMAGE') {
          return false;
        }

        if (get().brandKitId && el.data.content === BrandVariable.LOGO) {
          return false;
        }

        const content = el.data.content.trim();

        return (
          content === '' ||
          (!content.startsWith('https://cdn.fresh8.co/') &&
            !content.startsWith(TEMPLATE_ASSETS_PREFIX))
        );
      });

      return imagesWithInvalidLinks.length > 0;
    },
    validateElementContents: () => {
      const state = get();
      const dataSourceIds = state.dataSources.map((ds) => ds.id);
      const textElements = state.elements.filter(isTextElement);
      const imageElements = state.elements.filter(isImageElement);
      const teamAssetElements = state.elements.filter(isTeamAssetElement);
      const casinoAssetElements = state.elements.filter(isCasinoAssetElement);

      return (
        [...textElements, ...imageElements, ...teamAssetElements, ...casinoAssetElements].every(
          (el) =>
            el.data.content !== DATA_SOURCE_REMOVED &&
            validateContentDataSource(el.data.content, dataSourceIds),
        ) && state.getInvalidElementContentMappings().length === 0
      );
    },
    resetElementData: () =>
      set(() => ({
        elements: [],
        selectedElementId: null,
        dataSources: [],
      })),
    upsertDataSources: (desiredDataSourceTypes, allDataSources) => {
      const state = get();
      let newDatasources = state.dataSources.slice();

      const getDatasourceId = (dsType: string) => {
        const dataSource = allDataSources.find((dataSource) => dataSource.type === dsType);
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        return dataSource!.id;
      };

      // Add any new data sources
      desiredDataSourceTypes.forEach((type) => {
        const dataSourceExists = newDatasources.some((dataSource) => dataSource.type === type);
        if (!dataSourceExists) {
          newDatasources.push({
            type,
            dataType: Datasources.getDataTypeFromSourceType(type),
            id: getDatasourceId(type),
          });
        }
      });

      newDatasources = newDatasources
        // Remove any data sources that are not selected
        .filter((ds) => {
          return desiredDataSourceTypes.includes(ds.type);
        })
        .sort((a, b) => {
          return a.type.localeCompare(b.type);
        });

      return set({
        dataSources: newDatasources,
      });
    },
    setBusinessEntityId(id: number) {
      return set({
        businessEntityId: id,
      });
    },
    setImportSourceType(type: AdTemplate['importSourceType']) {
      set({
        importSourceType: type,
        ...(supportsTemplateAnimations(type) ? {} : { animation: null, animationPlaybackToken: 0 }),
      });
    },
    setImportSourceJSON(jsonString: string) {
      set({
        importSourceJSON: jsonString,
      });
    },
    setVideoProjectId(id: string | null) {
      set({
        videoProjectId: id,
      });
    },
    setBrandKitId(id: string | null) {
      set({
        brandKitId: id,
      });
    },
    setBrandKit(brandKit) {
      set({
        brandKit,
      });
    },
  } as const satisfies EditorStateActions;

  return actions;
}

const bracketsRegex = /\(([^)]+)\)/;

const removeNodesFromSVG = (set: StoreSet, get: Get) => (svgId: string, ids: Array<string>) => {
  const state = get();
  const elements = state.elements;

  const root = elements.filter(isSVGElement).find((el) => el.id === svgId);
  const _root = structuredClone(root);
  const rootIndex = elements.findIndex((el) => el.id === svgId);

  // handle better?
  if (!_root) return;

  const rootNode = SVG(_root.data.svg);

  for (let i = 0; i < ids.length; i++) {
    const id = ids[i];
    const svgEl = rootNode.findOne(
      '[id="' + id.replace(/["\\]/g, '\\$&').replace(/£/g, 'Â£') + '"]',
    );
    if (svgEl == null) {
      console.warn(`Could not find el with id #${id} when removing from svg`);
      continue;
    }
    svgEl.remove();

    // Remove any large base64 embeded images from <defs/>
    const fillValue = svgEl.attr('fill') ?? '';
    const patternId = getPatternIdFromFill(fillValue);

    if (patternId != null) {
      const patternEl = rootNode.findOne(patternId);
      if (patternEl == null) {
        continue;
      }
      const base64ImageId = patternEl.findOne('USE')?.attr('xlink:href');
      rootNode.findOne(base64ImageId)?.remove();
    }
  }
  // Update the svg string to reflect the changes made above
  const newString = rootNode.svg();
  _root.data.svg = newString;
  set((state) => ({
    ...state,
    elements: insertItem(state.elements, rootIndex, _root),
  }));
};

const getImageLinkForImageElementNameFromSVG =
  (set: StoreSet, get: Get) =>
  (svgId: string, name: string): string => {
    const state = get();
    const elements = state.elements;
    const root = elements.filter(isSVGElement).find((el) => el.id === svgId);
    const _root = structuredClone(root);

    if (!_root) return '';
    const rootNode = SVG(_root.data.svg);

    let svgEl = rootNode.findOne(
      '[id="' + name.replace(/["\\]/g, '\\$&').replace(/£/g, 'Â£') + '"]',
    );
    if (svgEl?.type === 'g') {
      svgEl = svgEl.findOne('rect');
    }
    if (svgEl) {
      const fillValue = svgEl.attr('fill') ?? '';
      const patternId = getPatternIdFromFill(fillValue);
      const patternEl = rootNode.findOne(patternId as string);
      const imageId = patternEl?.findOne('use')?.attr('xlink:href');

      return rootNode.findOne(imageId)?.attr('xlink:href') || '';
    }
    return '';
  };

const generateUniqueIDs = (set: StoreSet, get: Get) => (svgId: string) => {
  const state = get();
  const elements = state.elements;

  const root = elements.filter(isSVGElement).find((el) => el.id === svgId);
  const _root = structuredClone(root);
  const rootIndex = elements.findIndex((el) => el.id === svgId);

  // handle better?
  if (!_root) return;

  const rootNode = addPrefixToSvgIDs(_root.data.svg, nanoid(16));
  // Update the svg string to reflect the changes made above
  _root.data.svg = rootNode.svg();
  set((state) => ({
    ...state,
    elements: insertItem(state.elements, rootIndex, _root),
  }));

  elements.filter(isVectorElement).forEach((vectorElement) => {
    const _vectorElement = structuredClone(vectorElement);
    const vectorElementIndex = elements.findIndex((el) => el.id === vectorElement.id);

    const svgString = `<svg>${vectorElement.data.content}<defs>${
      vectorElement.data.effectFilters ?? ''
    }</defs></svg>`;
    const rootNode = addPrefixToSvgIDs(svgString, nanoid(16));
    // Update the svg string to reflect the changes made above
    let contentString = '';
    rootNode
      .children()
      .filter((element) => element.type !== 'defs')
      .forEach((element) => {
        contentString += element.svg();
      });
    _vectorElement.data.content = contentString;

    if (_vectorElement.data.effectFilters) {
      let effectFilters = '';
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      rootNode
        .findOne('defs')!
        .children()
        .forEach((filterElement) => (effectFilters += filterElement.svg()));
      _vectorElement.data.effectFilters = effectFilters;
    }

    set((state) => ({
      ...state,
      elements: insertItem(state.elements, vectorElementIndex, _vectorElement),
    }));
  });
};

function addPrefixToSvgIDs(svgString: string, prefix: string): Element {
  const rootNode = SVG(svgString);
  // add prefix to id to make it globally unique.
  // save the mapping for reference substitution.
  const originalIDs = new Map<string, string>();
  rootNode.each((i, children) => {
    const element = children[i];
    const id = element.attr('id');
    if (!id) {
      return;
    }
    const uniqueID = `${prefix}--${id}`;
    originalIDs.set(`#${id}`, `#${uniqueID}`);
    element.attr('id', uniqueID);
  }, true);

  // traverse all attribute to find if any reference exists
  // and global id mapping is also exists then replace it.
  rootNode.each((i, children) => {
    const element = children[i];
    Object.entries(element.attr()).forEach((x) => {
      const [name, value] = x;
      if (typeof value === 'string' || value instanceof String) {
        const newValue = value.replace(/#[\w_-]+/g, (x) => {
          const uniqueID = originalIDs.get(x);
          return uniqueID ? uniqueID : x;
        });
        element.attr(name, newValue);
      }
    });
  }, true);
  return rootNode;
}

function insertItem<T>(arr: Array<T>, itemIndex: number, newItem: T): Array<T> {
  return [...arr.slice(0, itemIndex), newItem, ...arr.slice(itemIndex + 1)];
}

// url(#pattern1) --> #pattern1
function getPatternIdFromFill(wrappedFillValue: string) {
  const matches = wrappedFillValue.match(bracketsRegex);
  if (matches == null || matches.length < 2) {
    return null;
  }
  return matches[1];
}

const bracesRegex = /{([^{}]+)}/g;

function getElementContent(element: AdElement): string | null {
  if (
    isTextElement(element) ||
    isImageElement(element) ||
    isTeamAssetElement(element) ||
    isCasinoAssetElement(element)
  ) {
    return element.data.content;
  }

  return null;
}

const dataPointNamesBySourceType = new Map(
  Datasources.dataSourcesConfigs.map((config) => [
    config.type,
    new Set(config.points.map((point) => point.name)),
  ]),
);

export function getInvalidElementContentMappings(
  elements: AdElement[],
  dataSources: EditorState['dataSources'],
): InvalidElementContentMapping[] {
  const dataSourceById = new Map(dataSources.map((dataSource) => [dataSource.id, dataSource]));
  const invalidMappings: InvalidElementContentMapping[] = [];

  elements.forEach((element) => {
    const content = getElementContent(element);
    if (!content) return;

    [...content.matchAll(bracesRegex)].forEach((match) => {
      const placeholder = match[1];
      if (!placeholder.includes(':')) return;

      const [sourceId, dataPointName] = placeholder.split(':');
      const dataSource = dataSourceById.get(sourceId);

      if (!dataSource) {
        invalidMappings.push({
          elementId: element.id,
          elementName: element.name,
          elementType: element.type,
          content,
          sourceId,
          dataPointName,
          reason: 'missing-source',
        });
        return;
      }

      const validDataPointNames = dataPointNamesBySourceType.get(dataSource.type);
      if (!validDataPointNames?.has(dataPointName as Datasources.DataPointNames[number])) {
        invalidMappings.push({
          elementId: element.id,
          elementName: element.name,
          elementType: element.type,
          content,
          sourceId,
          sourceType: dataSource.type,
          dataPointName,
          reason: 'invalid-data-point',
        });
      }
    });
  });

  return invalidMappings;
}

/**
 * Validates the datapoint source ids match data sources on the template
 * Example string: "{someid:DATA_POINT} some text {someotherid:DATA_POINT}"
 * @param content
 * @param sourceIds
 * @returns {Boolean}
 */
export function validateContentDataSource(content: string, sourceIds: Array<string>) {
  const matches = [...content.matchAll(bracesRegex)].map((match) => match[1]);
  if (matches == null || matches.length == 0) {
    return true;
  }
  return matches.every((match) => {
    const [sourceId] = match.split(':');
    return sourceIds.includes(sourceId);
  });
}
